import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabase'

const REPORT_TYPES = [
  { id: 'pipeline', label: 'Pipeline Summary', icon: '£' },
  { id: 'winrate', label: 'Win Rate Analysis', icon: 'W' },
  { id: 'activity', label: 'Bid Activity', icon: 'A' },
  { id: 'qualification', label: 'Qualification Health', icon: 'Q' },
  { id: 'resource', label: 'Resource Utilisation', icon: 'R' },
]

const PERIODS = [
  { id: 'month', label: 'This Month' },
  { id: 'quarter', label: 'This Quarter' },
  { id: 'year', label: 'This Year' },
  { id: 'all', label: 'All Time' },
]

function getDateRange(period) {
  const now = new Date()
  if (period === 'month') return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: new Date(now.getFullYear(), now.getMonth() + 1, 0) }
  if (period === 'quarter') { const q = Math.floor(now.getMonth() / 3); return { from: new Date(now.getFullYear(), q * 3, 1), to: new Date(now.getFullYear(), q * 3 + 3, 0) } }
  if (period === 'year') return { from: new Date(now.getFullYear(), 0, 1), to: new Date(now.getFullYear(), 11, 31) }
  return null
}

function StatCard({ label, value, sub, colour }) {
  return (
    <div style={{ background: 'var(--white)', border: '1px solid var(--green-light)', padding: '20px 24px', borderLeft: `4px solid ${colour || 'var(--green)'}` }}>
      <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--slate)', marginBottom: '8px' }}>{label}</div>
      <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.5rem', fontWeight: 700, color: colour || 'var(--green-dark)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.8125rem', color: 'var(--slate)', marginTop: '6px' }}>{sub}</div>}
    </div>
  )
}

function SectionHeader({ title }) {
  return (
    <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.25rem', fontWeight: 600, color: 'var(--green-dark)', borderBottom: '2px solid var(--green-light)', paddingBottom: '8px', marginBottom: '16px', marginTop: '28px' }}>
      {title}
    </div>
  )
}

function BarChart({ data, colour }) {
  if (!data || data.length === 0) return <p style={{ color: 'var(--slate)', fontSize: '0.875rem' }}>No data available.</p>
  const max = Math.max(...data.map(d => d.value), 1)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {data.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '140px', fontSize: '0.8125rem', color: 'var(--charcoal)', flexShrink: 0, textAlign: 'right' }}>{item.label}</div>
          <div style={{ flex: 1, height: '24px', background: 'var(--off-white)', position: 'relative' }}>
            <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${(item.value / max) * 100}%`, background: colour || 'var(--green)', transition: 'width 0.3s' }} />
          </div>
          <div style={{ width: '40px', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--charcoal)', flexShrink: 0 }}>{item.value}</div>
        </div>
      ))}
    </div>
  )
}

function ReportingSuite({ session, profile, onNavigate }) {
  const orgId = profile?.organisation_id
  const reportRef = useRef(null)
  const [activeReport, setActiveReport] = useState('pipeline')
  const [period, setPeriod] = useState('all')
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [data, setData] = useState({})

  const canAccess = profile?.role === 'super_admin' || profile?.role === 'admin' || profile?.role === 'head_of_bids'
  console.log('ReportingSuite profile:', profile?.role, 'canAccess:', canAccess, 'orgId:', orgId)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (orgId && canAccess) loadReport(activeReport, period)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReport, period, orgId])

  const loadReport = async (report, per) => {
    setLoading(true)
    const range = getDateRange(per)

    if (report === 'pipeline') await loadPipelineReport(range)
    if (report === 'winrate') await loadWinRateReport(range)
    if (report === 'activity') await loadActivityReport(range)
    if (report === 'qualification') await loadQualificationReport(range)
    if (report === 'resource') await loadResourceReport(range)

    setLoading(false)
  }

  const loadPipelineReport = async (range) => {
    let q = supabase.from('opportunities').select('stage, contract_value_min, contract_value_max, sector, created_at').eq('organisation_id', orgId)
    if (range) q = q.gte('created_at', range.from.toISOString()).lte('created_at', range.to.toISOString())
    const { data: opps } = await q

    const STAGES = ['identified', 'qualifying', 'pursuing', 'won', 'lost', 'no_bid']
    const STAGE_LABELS = { identified: 'Identified', qualifying: 'Qualifying', pursuing: 'Pursuing', won: 'Won', lost: 'Lost', no_bid: 'No Bid' }

    const byStageCounts = STAGES.map(s => ({ label: STAGE_LABELS[s], value: (opps || []).filter(o => o.stage === s).length })).filter(s => s.value > 0)

    const sectorCounts = {}
    ;(opps || []).forEach(o => { if (o.sector) sectorCounts[o.sector] = (sectorCounts[o.sector] || 0) + 1 })
    const bySector = Object.entries(sectorCounts).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value)

    const totalValue = (opps || []).reduce((sum, o) => {
      const mid = ((o.contract_value_min || 0) + (o.contract_value_max || o.contract_value_min || 0)) / 2
      return sum + mid
    }, 0)

    const pursuing = (opps || []).filter(o => o.stage === 'pursuing').length
    const won = (opps || []).filter(o => o.stage === 'won').length
    const decided = (opps || []).filter(o => o.stage === 'won' || o.stage === 'lost').length

    setData(prev => ({
      ...prev, pipeline: {
        total: (opps || []).length,
        pursuing,
        won,
        winRate: decided > 0 ? Math.round((won / decided) * 100) : null,
        totalValue,
        byStage: byStageCounts,
        bySector
      }
    }))
  }

  const loadWinRateReport = async (range) => {
    let q = supabase.from('opportunities').select('stage, contract_value_min, contract_value_max, sector, created_at').eq('organisation_id', orgId).in('stage', ['won', 'lost'])
    if (range) q = q.gte('created_at', range.from.toISOString()).lte('created_at', range.to.toISOString())
    const { data: decided } = await q

    const won = (decided || []).filter(o => o.stage === 'won').length
    const lost = (decided || []).filter(o => o.stage === 'lost').length
    const total = won + lost
    const rate = total > 0 ? Math.round((won / total) * 100) : null

    const bySector = {}
    ;(decided || []).forEach(o => {
      if (!o.sector) return
      if (!bySector[o.sector]) bySector[o.sector] = { won: 0, total: 0 }
      bySector[o.sector].total++
      if (o.stage === 'won') bySector[o.sector].won++
    })
    const bySectorArr = Object.entries(bySector).map(([label, v]) => ({ label: `${label} (${Math.round((v.won/v.total)*100)}%)`, value: v.won })).sort((a, b) => b.value - a.value)

    const wonValue = (decided || []).filter(o => o.stage === 'won').reduce((sum, o) => {
      return sum + ((o.contract_value_min || 0) + (o.contract_value_max || o.contract_value_min || 0)) / 2
    }, 0)

    setData(prev => ({ ...prev, winrate: { won, lost, total, rate, wonValue, bySector: bySectorArr } }))
  }

  const loadActivityReport = async (range) => {
    let qOpps = supabase.from('opportunities').select('created_at, stage').eq('organisation_id', orgId)
    if (range) qOpps = qOpps.gte('created_at', range.from.toISOString())
    const { data: opps } = await qOpps

    const { data: plans } = await supabase.from('bid_plans').select('id, organisation_id').eq('organisation_id', orgId)
    const planIds = (plans || []).map(p => p.id)

    let overdueMs = 0
    let upcomingMs = 0
    if (planIds.length > 0) {
      const { data: ms } = await supabase.from('milestones').select('due_date, status').in('bid_plan_id', planIds)
      const now = new Date()
      const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
      overdueMs = (ms || []).filter(m => m.status !== 'complete' && new Date(m.due_date) < now).length
      upcomingMs = (ms || []).filter(m => m.status !== 'complete' && new Date(m.due_date) >= now && new Date(m.due_date) <= in30).length
    }

    const { data: actions } = await supabase.from('actions').select('status, due_date').eq('organisation_id', orgId)
    const overdueActions = (actions || []).filter(a => a.status !== 'complete' && a.status !== 'cancelled' && a.due_date && new Date(a.due_date) < new Date()).length

    setData(prev => ({ ...prev, activity: { newOpps: (opps || []).length, overdueMs, upcomingMs, overdueActions } }))
  }

  const loadQualificationReport = async (range) => {
    let q = supabase.from('qualification_scores').select('overall_score, recommendation, pwin_adjusted, pwin_estimate').eq('organisation_id', orgId)
    const { data: scores } = await q

    const pursue = (scores || []).filter(s => s.recommendation === 'pursue').length
    const conditional = (scores || []).filter(s => s.recommendation === 'conditional').length
    const noBid = (scores || []).filter(s => s.recommendation === 'no_bid').length
    const avgScore = scores && scores.length > 0 ? (scores.reduce((sum, s) => sum + parseFloat(s.overall_score || 0), 0) / scores.length).toFixed(2) : null

    const withPwin = (scores || []).filter(s => s.pwin_adjusted && s.pwin_estimate)
    const avgPwin = withPwin.length > 0 ? Math.round(withPwin.reduce((sum, s) => sum + parseFloat(s.pwin_adjusted), 0) / withPwin.length) : null

    setData(prev => ({ ...prev, qualification: { total: (scores || []).length, pursue, conditional, noBid, avgScore, avgPwin, byRec: [{ label: 'Pursue', value: pursue }, { label: 'Conditional', value: conditional }, { label: 'No Bid', value: noBid }].filter(r => r.value > 0) } }))
  }

  const loadResourceReport = async (range) => {
    const { data: profiles } = await supabase.from('profiles').select('id, full_name, username').eq('organisation_id', orgId).eq('is_active', true)

    const { data: opps } = await supabase.from('opportunities').select('bid_lead_id, stage').eq('organisation_id', orgId).in('stage', ['qualifying', 'pursuing'])

    const { data: actions } = await supabase.from('actions').select('assigned_to, status').eq('organisation_id', orgId).in('status', ['open', 'in_progress'])

    const { data: questions } = await supabase.from('questions').select('assigned_to, status').eq('organisation_id', orgId).neq('status', 'complete')

    const userMap = {}
    ;(profiles || []).forEach(p => { userMap[p.id] = { name: p.full_name || p.username, bids: 0, actions: 0, questions: 0 } })

    ;(opps || []).forEach(o => { if (o.bid_lead_id && userMap[o.bid_lead_id]) userMap[o.bid_lead_id].bids++ })
    ;(actions || []).forEach(a => { if (a.assigned_to && userMap[a.assigned_to]) userMap[a.assigned_to].actions++ })
    ;(questions || []).forEach(q => { if (q.assigned_to && userMap[q.assigned_to]) userMap[q.assigned_to].questions++ })

    const rows = Object.values(userMap).filter(u => u.bids > 0 || u.actions > 0 || u.questions > 0)

    setData(prev => ({ ...prev, resource: { rows } }))
  }

  const handleExportPDF = async () => {
    setExporting(true)
    try {
      const { default: jsPDF } = await import('jspdf')
      const { default: html2canvas } = await import('html2canvas')

      const element = reportRef.current
      const canvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: '#ffffff' })
      const imgData = canvas.toDataURL('image/png')

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width

      const orgName = profile?.organisations?.name || 'Cadence'
      const primaryColour = profile?.organisations?.primary_colour || '2D6A4F'
      const r = parseInt(primaryColour.slice(0, 2), 16)
      const g = parseInt(primaryColour.slice(2, 4), 16)
      const b = parseInt(primaryColour.slice(4, 6), 16)

      // Cover header
      pdf.setFillColor(r, g, b)
      pdf.rect(0, 0, pdfWidth, 20, 'F')
      pdf.setTextColor(255, 255, 255)
      pdf.setFontSize(14)
      pdf.setFont('helvetica', 'bold')
      pdf.text(orgName, 10, 13)
      pdf.setFontSize(10)
      pdf.setFont('helvetica', 'normal')
      pdf.text(REPORT_TYPES.find(r => r.id === activeReport)?.label + ' — ' + new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }), pdfWidth - 10, 13, { align: 'right' })

      // Report content
      const contentY = 25
      const availableHeight = pdf.internal.pageSize.getHeight() - contentY - 15

      if (pdfHeight <= availableHeight) {
        pdf.addImage(imgData, 'PNG', 0, contentY, pdfWidth, pdfHeight)
      } else {
        let remainingHeight = pdfHeight
        let sourceY = 0
        let isFirstPage = true

        while (remainingHeight > 0) {
          const pageContentHeight = isFirstPage ? availableHeight : pdf.internal.pageSize.getHeight() - 25
          const sliceHeight = Math.min(remainingHeight, pageContentHeight)
          const canvasSliceHeight = (sliceHeight / pdfHeight) * canvas.height
          const canvasSliceY = (sourceY / pdfHeight) * canvas.height

          const sliceCanvas = document.createElement('canvas')
          sliceCanvas.width = canvas.width
          sliceCanvas.height = canvasSliceHeight
          const ctx = sliceCanvas.getContext('2d')
          ctx.drawImage(canvas, 0, canvasSliceY, canvas.width, canvasSliceHeight, 0, 0, canvas.width, canvasSliceHeight)
          const sliceData = sliceCanvas.toDataURL('image/png')

          pdf.addImage(sliceData, 'PNG', 0, isFirstPage ? contentY : 20, pdfWidth, sliceHeight)

          remainingHeight -= sliceHeight
          sourceY += sliceHeight

          if (remainingHeight > 0) {
            pdf.addPage()
            pdf.setFillColor(r, g, b)
            pdf.rect(0, 0, pdfWidth, 15, 'F')
            pdf.setTextColor(255, 255, 255)
            pdf.setFontSize(9)
            pdf.text(orgName + ' — ' + REPORT_TYPES.find(r => r.id === activeReport)?.label, 10, 10)
            isFirstPage = false
          }
        }
      }

      // Footer
      const pageCount = pdf.internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i)
        pdf.setFontSize(8)
        pdf.setTextColor(150, 150, 150)
        pdf.text(`Page ${i} of ${pageCount}`, pdfWidth - 10, pdf.internal.pageSize.getHeight() - 8, { align: 'right' })
        if (profile?.organisations?.powered_by_cadence !== false) {
          pdf.text('Powered by Cadence', 10, pdf.internal.pageSize.getHeight() - 8)
        }
      }

      pdf.save(`${orgName} — ${REPORT_TYPES.find(r => r.id === activeReport)?.label} — ${new Date().toLocaleDateString('en-GB')}.pdf`)
    } catch (err) {
      console.error('PDF export error:', err)
    }
    setExporting(false)
  }

  if (!canAccess) {
    return (
      <div className="scorecard-page container">
        <div className="page-header">
          <p className="label">Intelligence</p>
          <h1>Reporting & Analytics</h1>
        </div>
        <div className="card">
          <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '32px 0' }}>
            Reporting is available to Admins and Head of Bid Management only.
          </p>
        </div>
      </div>
    )
  }

  const d = data[activeReport] || {}

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px' }}>

      <div className="page-header">
        <p className="label">Intelligence</p>
        <h1>Reporting & Analytics</h1>
        <p>Live performance data for {profile?.organisations?.name || 'your organisation'}.</p>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {REPORT_TYPES.map(r => (
            <button
              key={r.id}
              onClick={() => setActiveReport(r.id)}
              style={{
                padding: '6px 14px', fontSize: '0.8125rem', fontWeight: 600,
                background: activeReport === r.id ? 'var(--green-dark)' : 'none',
                border: `1px solid ${activeReport === r.id ? 'var(--green-dark)' : 'var(--silver)'}`,
                color: activeReport === r.id ? 'var(--white)' : 'var(--slate)',
                cursor: 'pointer', transition: 'all 0.15s'
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {PERIODS.map(p => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              style={{
                padding: '5px 12px', fontSize: '0.75rem', fontWeight: 600,
                background: period === p.id ? 'var(--green)' : 'none',
                border: `1px solid ${period === p.id ? 'var(--green)' : 'var(--silver)'}`,
                color: period === p.id ? 'var(--white)' : 'var(--slate)',
                cursor: 'pointer'
              }}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={handleExportPDF}
            disabled={exporting || loading}
            style={{
              padding: '5px 16px', fontSize: '0.75rem', fontWeight: 600,
              background: 'var(--green-dark)', border: 'none',
              color: 'var(--white)', cursor: 'pointer', marginLeft: '8px',
              opacity: exporting || loading ? 0.6 : 1
            }}
          >
            {exporting ? 'Exporting...' : 'Export PDF'}
          </button>
        </div>
      </div>

      {/* Report content */}
      <div ref={reportRef} style={{ background: 'var(--off-white)', padding: '24px', minHeight: '400px' }}>

        {loading && <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '48px 0' }}>Loading report data...</p>}

        {!loading && activeReport === 'pipeline' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
              <StatCard label="Total Opportunities" value={d.total || 0} />
              <StatCard label="Active Pursuits" value={d.pursuing || 0} colour="var(--green)" />
              <StatCard label="Won" value={d.won || 0} colour="var(--green-dark)" />
              <StatCard label="Win Rate" value={d.winRate !== null && d.winRate !== undefined ? d.winRate + '%' : '—'} colour={d.winRate >= 50 ? 'var(--green)' : d.winRate >= 25 ? 'var(--amber)' : 'var(--red)'} />
            </div>
            {d.totalValue > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <StatCard label="Total Pipeline Value (midpoint estimate)" value={'£' + Math.round(d.totalValue).toLocaleString()} colour="var(--green-mid)" />
              </div>
            )}
            <SectionHeader title="Opportunities by Stage" />
            <BarChart data={d.byStage} colour="var(--green)" />
            {d.bySector && d.bySector.length > 0 && (
              <>
                <SectionHeader title="Opportunities by Sector" />
                <BarChart data={d.bySector} colour="var(--green-mid)" />
              </>
            )}
          </div>
        )}

        {!loading && activeReport === 'winrate' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
              <StatCard label="Decided Bids" value={d.total || 0} />
              <StatCard label="Won" value={d.won || 0} colour="var(--green)" />
              <StatCard label="Lost" value={d.lost || 0} colour="var(--red)" />
              <StatCard label="Win Rate" value={d.rate !== null && d.rate !== undefined ? d.rate + '%' : '—'} colour={d.rate >= 50 ? 'var(--green)' : d.rate >= 25 ? 'var(--amber)' : 'var(--red)'} />
            </div>
            {d.wonValue > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <StatCard label="Total Value Won (midpoint estimate)" value={'£' + Math.round(d.wonValue).toLocaleString()} colour="var(--green-dark)" />
              </div>
            )}
            {d.bySector && d.bySector.length > 0 && (
              <>
                <SectionHeader title="Wins by Sector" />
                <BarChart data={d.bySector} colour="var(--green)" />
              </>
            )}
          </div>
        )}

        {!loading && activeReport === 'activity' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
              <StatCard label="New Opportunities" value={d.newOpps || 0} colour="var(--green)" sub={period === 'all' ? 'All time' : 'In selected period'} />
              <StatCard label="Actions Overdue" value={d.overdueActions || 0} colour={d.overdueActions > 0 ? 'var(--red)' : 'var(--green)'} />
              <StatCard label="Milestones Overdue" value={d.overdueMs || 0} colour={d.overdueMs > 0 ? 'var(--red)' : 'var(--green)'} />
              <StatCard label="Milestones Due in 30 Days" value={d.upcomingMs || 0} colour={d.upcomingMs > 5 ? 'var(--amber)' : 'var(--green)'} />
            </div>
          </div>
        )}

        {!loading && activeReport === 'qualification' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
              <StatCard label="Total Qualifications" value={d.total || 0} />
              <StatCard label="Average Score" value={d.avgScore || '—'} sub="out of 5.00" colour="var(--green)" />
              <StatCard label="Average PWin" value={d.avgPwin !== null && d.avgPwin !== undefined ? d.avgPwin + '%' : '—'} colour="var(--green-mid)" />
              <StatCard label="Pursue Rate" value={d.total > 0 ? Math.round((d.pursue / d.total) * 100) + '%' : '—'} colour="var(--green-dark)" />
            </div>
            {d.byRec && d.byRec.length > 0 && (
              <>
                <SectionHeader title="Recommendations" />
                <BarChart data={d.byRec} colour="var(--green)" />
              </>
            )}
          </div>
        )}

        {!loading && activeReport === 'resource' && (
          <div>
            {(!d.rows || d.rows.length === 0) ? (
              <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '32px 0' }}>No active resource data available.</p>
            ) : (
              <>
                <SectionHeader title="Active Workload by User" />
                <div style={{ background: 'var(--white)', border: '1px solid var(--green-light)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', background: 'var(--green-dark)', padding: '10px 16px' }}>
                    {['Team Member', 'Active Bids', 'Open Actions', 'Open Questions'].map(h => (
                      <div key={h} style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)' }}>{h}</div>
                    ))}
                  </div>
                  {d.rows.map((row, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '10px 16px', borderBottom: '1px solid var(--green-light)', background: i % 2 === 0 ? 'var(--white)' : 'var(--off-white)' }}>
                      <div style={{ fontWeight: 600, color: 'var(--charcoal)', fontSize: '0.9375rem' }}>{row.name}</div>
                      <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', fontWeight: 700, color: row.bids > 5 ? 'var(--amber)' : 'var(--green-dark)', lineHeight: 1 }}>{row.bids}</div>
                      <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', fontWeight: 700, color: row.actions > 10 ? 'var(--red)' : 'var(--green-dark)', lineHeight: 1 }}>{row.actions}</div>
                      <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', fontWeight: 700, color: row.questions > 10 ? 'var(--amber)' : 'var(--green-dark)', lineHeight: 1 }}>{row.questions}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="actions" style={{ marginTop: '16px' }}>
        <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>← Dashboard</button>
      </div>
    </div>
  )
}

export default ReportingSuite
